/* In-app tutorial system (DESIGN-tutorial-system.md).
 *
 * Content is data (steps.student.ts / steps.instructor.ts): each step names a
 * screen + a `data-tut` anchor and copies its title/body from the design doc.
 * Predicates evaluate against a TutCtx snapshot built by the host each render,
 * so steps advance when the user actually performs the action — and the
 * mission-panel checklist tolerates wandering (a task checks off no matter
 * which step is current). */
import type { RoundDecisions } from '@venturewrist/shared';

export type TutSeriesId = 'student' | 'instructor';

/** Live snapshot the predicates read. Built by the host component per render. */
export interface TutCtx {
  /** Active tab id (game-shell tab or instructor-console tab). */
  tab: string;
  /** Staged decisions draft (student series; `{}` for the instructor). */
  draft: RoundDecisions;
  /** Working game — server state + staged draft (student series). */
  game: unknown | null;
  maxRounds: number;
  /** Formatted segment reference price for the {refPrice} token. */
  refPrice: string;
  /** Event counters screens emit via tutSignal() (e.g. 'design-edit'). */
  signals: Record<string, number>;
  /** The quarter-results modal is on screen. */
  resultOpen: boolean;
  /** At least one quarter has resolved. */
  submitted: boolean;
  /** Instructor review drilldown is open. */
  drilldownOpen: boolean;
  /** Instructor series only: the class currently on screen. Copy adapts to it —
   *  a fresh class has no students and no leaderboard, so steps written for the
   *  populated demo seed would otherwise describe things that aren't there. */
  className?: string;
  /** Enrolled students in that class. */
  studentCount?: number;
  /** Of those, how many have played at least one quarter. */
  playedCount?: number;
}

export interface TutStep {
  id: string;
  /** Tab this step's anchor lives on. When the user is on a different tab the
   *  spotlight points at the tab button instead (arrow-toast behaviour). */
  screen?: string;
  /** data-tut attribute of the element to highlight; centered card if absent. */
  anchor?: string;
  title?: string;
  /** Card copy. {maxRounds} / {refPrice} / {className} tokens are resolved at
   *  render. A function form lets a step rewrite itself for the live context
   *  (e.g. a class with no students yet). */
  body: string | ((ctx: TutCtx) => string);
  /** Event-driven advance: the step completes when this flips false→true while
   *  current. Already-true at entry shows a "✓ done" Next instead of skipping,
   *  so the teaching beat still lands. Steps without it advance on Next only. */
  done?: (ctx: TutCtx) => boolean;
}

/** Mission-panel checklist item; sticky once achieved. */
export interface TutTask {
  id: string;
  label: string | ((ctx: TutCtx) => string);
  done: (ctx: TutCtx) => boolean;
  /** Task only belongs on the checklist when this holds. Omitted = always.
   *  Keeps unachievable tasks off the panel (no students to drill into yet). */
  when?: (ctx: TutCtx) => boolean;
}

export interface TutChapter {
  id: string;
  title: string;
  steps: TutStep[];
  tasks: TutTask[];
}

export interface TutSeries {
  id: TutSeriesId;
  title: string;
  /** data-tut of the tab button for a screen id ('tab-…' / 'ic-tab-…'). */
  tabAnchor: (screen: string) => string;
  /** Arrow-toast copy shown when the user must switch tabs to continue. */
  tabHint: (screen: string) => string;
  chapters: TutChapter[];
  /** Mission-panel "next steps" shown after the final step. */
  finale?: { title: string; items: string[] };
}

/** Steps-per-chapter shape the store uses to advance/complete. */
export const shapeOf = (s: TutSeries): number[] => s.chapters.map((c) => c.steps.length);
